import { useState, useEffect, createContext, useContext } from 'react';
import { authAPI } from '../services/api';
import { isSessionError } from '../services/sessionError';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [sessionError, setSessionError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setSessionError(false);
    const token = localStorage.getItem('token');
    if (token) {
      authAPI.me()
        .then(res => {
          if (!current) return;
          if (localStorage.getItem('token') !== token) {
            setRetry(n => n + 1);
            return;
          }
          setUser(res.data);
        })
        .catch(error => {
          if (!current) return;
          if (localStorage.getItem('token') !== token) {
            setRetry(n => n + 1);
            return;
          }
          if (isSessionError(error)) {
            localStorage.removeItem('token');
            setUser(null);
          } else {
            setSessionError(true);
          }
        })
        .finally(() => { if (current) setLoading(false); });
    } else {
      setLoading(false);
    }
    return () => { current = false; };
  }, [retry]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const refresh = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      authAPI.me().then(res => {
        if (alive && localStorage.getItem("token") === token) setUser(res.data);
      }).catch(() => {});
    };
    const timer = setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [user?.id]);

  useEffect(() => {
    if (user?.role !== 'familiarization') return;
    const check = () => {
      const deadline = Date.parse(user.access_expires_at ? user.access_expires_at.replace(/Z?$/, 'Z') : '');
      if (!Number.isFinite(deadline) || Date.now() >= deadline) {
        localStorage.removeItem('token'); setUser(null);
      }
    };
    check();
    const timer = setInterval(check, 1000);
    window.addEventListener('focus', check);
    return () => { clearInterval(timer); window.removeEventListener('focus', check); };
  }, [user?.role, user?.access_expires_at]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('token', res.data.access_token);
    const me = await authAPI.me();
    setUser(me.data);
    return me.data;
  };

  const register = async (email, password, name) => {
    await authAPI.register({ email, password, name });
    return login(email, password);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  if (sessionError && !loading) {
    return (
      <div role="alert" style={{ padding: 40, color: 'var(--text-secondary)' }}>
        <p>Не удалось проверить вход. Сохранённая сессия не удалена.</p>
        <p>Проверьте соединение и повторите попытку.</p>
        <button className="btn btn-primary" onClick={() => { setLoading(true); setSessionError(false); setRetry(n => n + 1); }}>Повторить проверку</button>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);